import { ButtonProps } from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import GroupMemberRecord from "models/view-models/group-member-record";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import { ModalTypes } from "molecules/constants/modal-types";
import BaseModalFooter from "molecules/modals/footers/base-modal-footer";
import BaseModalHeader from "molecules/modals/headers/base-modal-header";
import MobileModalHeader from "molecules/modals/headers/mobile-modal-header";
import Modal, { ModalProps } from "molecules/modals/modal";
import React, { useState } from "react";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint from "utilities/hooks/use-breakpoint";
import useModalTransition from "utilities/hooks/use-modal-transition";
import { t } from "utilities/localization-utils";
import PreviewTeamMembersList from "./preview-team-members-list";
import InputFormField from "molecules/form-fields/input-form-field";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface PreviewTeamMembersModalProps
    extends Omit<ModalProps, "closeDialog" | "type"> {
    closeDialog: () => void;
    groupName: string;
    groupMembers?: GroupMemberRecord[];
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS_NAME = "c-preview-team-members-modal";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const PreviewTeamMembersModal: React.FunctionComponent<
    PreviewTeamMembersModalProps
> = (props: PreviewTeamMembersModalProps) => {
    const { groupName, groupMembers, closeDialog } = props;

    const [searchText, setSearchText] = useState("");

    const closeButtonText = t("close");

    const transition = useModalTransition();
    const isDesktop = useBreakpoint(Breakpoints.Phone);
    const modalType = isDesktop ? ModalTypes.Base : ModalTypes.Bottom;

    const heading = t("teamMembersForTeamName", { teamName: groupName });

    const header = isDesktop ? (
        <BaseModalHeader closeDialog={closeDialog} heading={heading} />
    ) : (
        <MobileModalHeader closeDialog={closeDialog} heading={heading} />
    );

    const modalFooterPrimaryButtonProps: ButtonProps = {
        size: isDesktop ? ButtonSizes.Medium : ButtonSizes.Small,
        onClick: closeDialog,
    };

    const filteredGroupMembers = groupMembers?.filter((member) =>
        member
            .getFirstAndLastName()
            .toLowerCase()
            .includes(searchText.toLowerCase())
    );

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    return (
        <Modal
            {...props}
            closeButtonStyle={ModalCloseButtonStyle.Hidden}
            closeDialog={closeDialog}
            cssClassName={BASE_CLASS_NAME}
            transition={transition}
            type={modalType}>
            {header}
            <div className={`${BASE_CLASS_NAME}__body`}>
                <InputFormField
                    value={searchText}
                    required={false}
                    isValid={true}
                    label={t("filterBy", { item: t("name") })}
                    onChange={handleSearchChange}
                />
                <PreviewTeamMembersList
                    groupName={groupName}
                    groupMembers={filteredGroupMembers}
                />
            </div>
            <BaseModalFooter
                closeDialog={closeDialog}
                primaryButtonProps={modalFooterPrimaryButtonProps}
                primaryText={closeButtonText}
            />
        </Modal>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default PreviewTeamMembersModal;

// #endregion Exports
